/**
 * Режим указания ячеек при вводе формулы (= …), как в Calc/Excel.
 */
import {
  formatCellRef,
  formatColRangeRef,
  colLabel,
  parseCellRef,
  parseRangeRef,
} from './formulaEngine.js';

const BEFORE_POINT = new Set(['=', '(', ';', ',', '+', '-', '*', '/', ':']);

/** A1, A1:B2 или столбцы A:A / A:C */
const REF_TOKEN_RE = /\$?[A-Za-z]+\$?\d+(?::\$?[A-Za-z]+\$?\d+)?|\$?[A-Za-z]+:\$?[A-Za-z]+/g;

/**
 * Можно ли со стрелки/клика вставить ссылку при caret в draft.
 * draft должен начинаться с '='.
 */
export function canPointInsert(draft, caret) {
  const s = String(draft || '');
  if (!s.startsWith('=')) return false;
  const pos = Math.max(0, Math.min(Number(caret) || 0, s.length));
  // внутри кавычек — не указываем
  let inStr = false;
  for (let i = 0; i < pos; i += 1) {
    if (s[i] === '"') {
      if (s[i + 1] === '"') {
        i += 1;
        continue;
      }
      inStr = !inStr;
    }
  }
  if (inStr) return false;

  // если caret внутри уже выделенного нами ref — можно двигать (caller знает pointing)
  let i = pos - 1;
  while (i >= 0 && s[i] === ' ') i -= 1;
  if (i < 0) return s === '=' || s.startsWith('=');
  const ch = s[i];
  if (BEFORE_POINT.has(ch)) return true;
  // caret сразу после буквенно-цифровой ссылки — тоже ок (замена текущей)
  if (/[A-Za-z0-9$]/.test(ch)) return true;
  return false;
}

/** Диапазон токена ссылки вокруг caret (или null). */
export function refTokenAround(draft, caret) {
  const s = String(draft || '');
  const pos = Math.max(0, Math.min(Number(caret) || 0, s.length));
  let start = pos;
  let end = pos;
  while (start > 0 && /[A-Za-z0-9$:]/.test(s[start - 1])) start -= 1;
  while (end < s.length && /[A-Za-z0-9$:]/.test(s[end])) end += 1;
  const token = s.slice(start, end);
  const okCell = /^\$?[A-Za-z]+\$?\d+(?::\$?[A-Za-z]+\$?\d+)?$/.test(token);
  const okCol = /^\$?[A-Za-z]+:\$?[A-Za-z]+$/.test(token);
  if (!okCell && !okCol) return null;
  return { start, end, token };
}

export function formatRangeRef(r0, c0, r1, c1) {
  const a = formatCellRef({ row: r0, col: c0 });
  const b = formatCellRef({ row: r1, col: c1 });
  if (r0 === r1 && c0 === c1) return a;
  return `${a}:${b}`;
}

function resolveReplaceSpan(s, caret, pointing) {
  if (pointing && Number.isFinite(pointing.refStart) && Number.isFinite(pointing.refEnd)) {
    return { start: pointing.refStart, end: pointing.refEnd };
  }
  const tok = refTokenAround(s, caret);
  if (tok) return { start: tok.start, end: tok.end };
  const start = Math.max(0, Math.min(Number(caret) || 0, s.length));
  return { start, end: start };
}

/**
 * Вставить или заменить ссылку на одну ячейку в draft.
 */
export function applyPointedRef(draft, caret, row, col, pointing) {
  const s = String(draft || '');
  const ref = formatCellRef({ row, col });
  const { start, end } = resolveReplaceSpan(s, caret, pointing);
  const next = s.slice(0, start) + ref + s.slice(end);
  const nextCaret = start + ref.length;
  return {
    draft: next,
    caret: nextCaret,
    pointing: {
      refStart: start,
      refEnd: start + ref.length,
      row,
      col,
      anchorRow: pointing?.anchorRow ?? row,
      anchorCol: pointing?.anchorCol ?? col,
    },
  };
}

/**
 * Вставить/заменить диапазон столбцов A:A или A:C (как клик по заголовкам в Excel).
 */
export function applyPointedColRange(draft, caret, anchorCol, col, pointing) {
  const s = String(draft || '');
  const c0 = Math.min(anchorCol, col);
  const c1 = Math.max(anchorCol, col);
  const ref = formatColRangeRef(c0, c1);
  const { start, end } = resolveReplaceSpan(s, caret, pointing);
  const next = s.slice(0, start) + ref + s.slice(end);
  const nextCaret = start + ref.length;
  return {
    draft: next,
    caret: nextCaret,
    pointing: {
      refStart: start,
      refEnd: start + ref.length,
      row: 0,
      col,
      anchorRow: 0,
      anchorCol,
      r0: 0,
      c0,
      r1: null,
      c1,
      kind: 'cols',
    },
  };
}

/**
 * Вставить/заменить диапазон A1:B2 (или одну ячейку, если углы совпали).
 * anchor — первая точка drag; row/col — текущая.
 */
export function applyPointedRange(draft, caret, anchorRow, anchorCol, row, col, pointing) {
  const s = String(draft || '');
  const r0 = Math.min(anchorRow, row);
  const r1 = Math.max(anchorRow, row);
  const c0 = Math.min(anchorCol, col);
  const c1 = Math.max(anchorCol, col);
  const ref = formatRangeRef(r0, c0, r1, c1);
  const { start, end } = resolveReplaceSpan(s, caret, pointing);
  const next = s.slice(0, start) + ref + s.slice(end);
  const nextCaret = start + ref.length;
  return {
    draft: next,
    caret: nextCaret,
    pointing: {
      refStart: start,
      refEnd: start + ref.length,
      row,
      col,
      anchorRow,
      anchorCol,
      r0,
      c0,
      r1,
      c1,
    },
  };
}

/**
 * Все ссылки A1 / A1:B2 в формуле (вне кавычек) для цветной подсветки.
 * @returns {{ r0:number,c0:number,r1:number,c1:number, colorIndex:number, start:number, end:number }[]}
 */
export function extractFormulaRefs(draft, opts = {}) {
  const s = String(draft || '');
  if (!s.startsWith('=')) return [];
  const sheetRows = Math.max(1, Number(opts.rows) || 50);
  const out = [];
  let inStr = false;
  let i = 1;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '"') {
      if (inStr && s[i + 1] === '"') {
        i += 2;
        continue;
      }
      inStr = !inStr;
      i += 1;
      continue;
    }
    if (inStr) {
      i += 1;
      continue;
    }
    REF_TOKEN_RE.lastIndex = i;
    const m = REF_TOKEN_RE.exec(s);
    if (!m || m.index !== i) {
      i += 1;
      continue;
    }
    const token = m[0];
    const before = s[i - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) {
      i += 1;
      continue;
    }
    const after = s[i + token.length];
    if (after && /[A-Za-z]/.test(after)) {
      i += 1;
      continue;
    }
    let r0;
    let c0;
    let r1;
    let c1;
    if (token.includes(':')) {
      const range = parseRangeRef(token, { rows: sheetRows });
      if (!range) {
        i += token.length;
        continue;
      }
      r0 = Math.min(range.a.row, range.b.row);
      r1 = Math.max(range.a.row, range.b.row);
      c0 = Math.min(range.a.col, range.b.col);
      c1 = Math.max(range.a.col, range.b.col);
    } else {
      const ref = parseCellRef(token);
      if (!ref) {
        i += token.length;
        continue;
      }
      r0 = r1 = ref.row;
      c0 = c1 = ref.col;
    }
    out.push({
      r0, c0, r1, c1,
      start: i,
      end: i + token.length,
      colorIndex: out.length % 6,
      kind: token.includes(':') && !/\d/.test(token.split(':')[0]) ? 'cols' : 'cells',
    });
    i += token.length;
  }
  return out;
}

export function movePoint(row, col, dr, dc, rows, cols) {
  return {
    row: Math.max(0, Math.min(rows - 1, row + dr)),
    col: Math.max(0, Math.min(cols - 1, col + dc)),
  };
}

export { formatCellRef, colLabel };
